import React, { useEffect, useMemo, useState } from 'react';
import ChannelTreeNode from './ChannelTreeNode';
import { buildChannelTree, filterTree, collectAllPaths } from './tree';
import { useSelectedChannels } from '../../contexts/SelectedChannelsContext';

export default function ChannelTree({ channels }) {
  const [filter, setFilter] = useState('');
  const [userExpanded, setUserExpanded] = useState(() => new Set());

  const fullTree = useMemo(() => buildChannelTree(channels || []), [channels]);
  const filteredTree = useMemo(() => filterTree(fullTree, filter), [fullTree, filter]);

  // When filtering, auto-expand all to reveal matches. Otherwise honor user toggles.
  const autoExpanded = useMemo(() => filter.trim() ? collectAllPaths(filteredTree) : new Set(), [filter, filteredTree]);
  const expandedPaths = useMemo(() => {
    if (filter.trim()) return autoExpanded;
    return userExpanded;
  }, [filter, autoExpanded, userExpanded]);

  const setExpandedPaths = (next) => {
    // Only track user-expanded paths when not filtering
    if (!filter.trim()) setUserExpanded(typeof next === 'function' ? next(userExpanded) : next);
  };

  const { clearAll, selectedChannels } = useSelectedChannels();

  useEffect(() => {
    // clear selection if channel universe changes (handled in context) — nothing to do here
  }, [channels]);

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Filter channels (e.g., x or rms)"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          style={{ width: '100%', padding: '6px 8px' }}
        />
        <button type="button" onClick={() => setFilter('')}>Clear</button>
        <button type="button" onClick={clearAll}>Clear Selection</button>
      </div>

      <div style={{ maxHeight: 420, overflow: 'auto', border: '1px solid #444', borderRadius: 8, padding: 8 }}>
        {filteredTree.children.length === 0
          ? <div style={{ opacity: 0.7 }}>No channels.</div>
          : filteredTree.children.map(node => (
              <ChannelTreeNode
                key={node.path}
                node={node}
                depth={0}
                expandedPaths={expandedPaths}
                setExpandedPaths={setExpandedPaths}
              />
            ))
        }
      </div>

      <div style={{ opacity: 0.8, fontSize: 12 }}>
        Selected: {selectedChannels.length}
      </div>
    </div>
  );
}
